/**
 * GLOW UP — Enhancements JavaScript
 * Thème Pitch — Performance, Animations, Wishlist, Pop-up, Dark Mode
 * Version: 1.0.0
 */

// ── 1. LAZY LOADING AVANCÉ ────────────────────────────────────
class GlowUpLazyLoader {
  constructor() {
    this.observer = null;
    this.init();
  }

  init() {
    if (!('IntersectionObserver' in window)) {
      this.loadAllImages();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.loadImage(entry.target);
            this.observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '200px 0px', // Précharge 200px avant l'arrivée
        threshold: 0.01,
      }
    );

    this.observeImages();
  }

  observeImages() {
    document.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.classList.add('lazy-img', 'is-loading');
      this.observer.observe(img);
    });
  }

  loadImage(img) {
    img.classList.remove('is-loading');

    if (img.complete) {
      img.classList.add('is-loaded');
      return;
    }

    img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
    img.addEventListener('error', () => img.classList.add('is-loaded'), { once: true });
  }

  loadAllImages() {
    document.querySelectorAll('img[data-src]').forEach((img) => {
      img.src = img.dataset.src;
    });
  }
}

// ── 2. SCROLL ANIMATIONS (IntersectionObserver) ───────────────
class GlowUpScrollAnimations {
  constructor() {
    this.observer = null;
    this.init();
  }

  init() {
    if (!('IntersectionObserver' in window)) return;

    // Ajouter la classe reveal aux éléments cibles
    this.addRevealClasses();

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Double rAF pour garantir que le browser a bien rendu l'état initial
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                entry.target.classList.add('is-visible');
              });
            });
            this.observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '0px 0px -80px 0px',
        threshold: 0.15,
      }
    );

    document.querySelectorAll('.will-animate').forEach((el) => {
      this.observer.observe(el);
    });
  }

  addRevealClasses() {
    const selectors = [
      // Produits & collections
      '.product-grid__item',
      '.collection-card',
      '.collection-list__item',
      // Hero
      '.hero__content',
      // Sections contenu
      '.media-with-content__content',
      '.media-with-content__media',
      '.featured-blog-posts__item',
      '.section-header',
      '.section__header',
      // Blocs groupe (Pitch theme)
      '.group-block-content',
      '.group-block > .group-block-content',
      // Email signup / newsletter
      '.email-signup',
      '[class*="email-signup"]',
      // Texte & contenu divers
      '.text-block',
      '.marquee',
      // Footer
      '.footer__block',
      '.footer__column',
      // Génériques — directs enfants de sections hors viewport
      '.shopify-section > section > *',
    ];

    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((el, index) => {
        if (el.classList.contains('will-animate')) return;
        const rect = el.getBoundingClientRect();
        const inViewport = rect.top < window.innerHeight && rect.bottom > 0;
        if (!inViewport) {
          // Hors écran : on prépare l'animation d'apparition
          el.classList.add('reveal', 'will-animate');
          el.style.transitionDelay = `${(index % 6) * 100}ms`;
        }
        // Si déjà visible → on ne touche à rien, l'élément reste visible
      });
    });
  }
}

// ── 3. DARK MODE ──────────────────────────────────────────────
class GlowUpDarkMode {
  constructor() {
    this.storageKey = 'glowup-theme';
    this.currentTheme = null;
    this.init();
  }

  init() {
    // Lire la préférence sauvegardée
    const saved = localStorage.getItem(this.storageKey);
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    this.currentTheme = saved || (prefersDark ? 'dark' : 'light');
    this.applyTheme(this.currentTheme);
    this.injectToggleButton();
    this.listenSystemChange();
  }

  applyTheme(theme) {
    document.documentElement.setAttribute('data-theme', theme);
    this.currentTheme = theme;
    localStorage.setItem(this.storageKey, theme);

    // Mise à jour des boutons toggle existants
    document.querySelectorAll('[data-dark-toggle]').forEach((btn) => {
      btn.setAttribute('aria-label', theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre');
    });
  }

  toggle() {
    const newTheme = this.currentTheme === 'dark' ? 'light' : 'dark';
    this.applyTheme(newTheme);
  }

  injectToggleButton() {
    // Injecter dans le header si pas déjà présent
    if (document.querySelector('[data-dark-toggle]')) return;

    const btn = document.createElement('button');
    btn.className = 'dark-mode-toggle';
    btn.setAttribute('data-dark-toggle', '');
    btn.setAttribute('aria-label', this.currentTheme === 'dark' ? 'Mode clair' : 'Mode sombre');
    btn.innerHTML = `
      <svg class="icon-moon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
      </svg>
      <svg class="icon-sun" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" xmlns="http://www.w3.org/2000/svg" style="display:none">
        <circle cx="12" cy="12" r="5"/>
        <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
        <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
      </svg>
    `;

    btn.addEventListener('click', () => this.toggle());

    // Mise à jour icônes selon thème
    this.updateIcons(btn);

    // Trouver le meilleur emplacement dans le header
    const headerActions = document.querySelector('.header__icons, .header__ctas, .header__actions');
    if (headerActions) {
      headerActions.prepend(btn);
    } else {
      // Fallback : position fixe
      btn.style.cssText = 'position:fixed;bottom:80px;right:16px;z-index:9998;box-shadow:0 4px 12px rgba(0,0,0,0.2)';
      document.body.appendChild(btn);
    }
  }

  updateIcons(btn) {
    const moon = btn.querySelector('.icon-moon');
    const sun = btn.querySelector('.icon-sun');
    if (this.currentTheme === 'dark') {
      moon.style.display = 'block';
      sun.style.display = 'none';
    } else {
      moon.style.display = 'none';
      sun.style.display = 'block';
    }
  }

  listenSystemChange() {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (!localStorage.getItem(this.storageKey)) {
        this.applyTheme(e.matches ? 'dark' : 'light');
      }
    });
  }
}

// ── 4. WISHLIST ───────────────────────────────────────────────
class GlowUpWishlist {
  constructor() {
    this.storageKey = 'glowup-wishlist';
    this.wishlist = new Set(JSON.parse(localStorage.getItem(this.storageKey) || '[]'));
    this.init();
  }

  init() {
    this.injectButtons();
    this.updateCounter();

    // Observer pour les nouveaux éléments (pagination infinie)
    const observer = new MutationObserver(() => this.injectButtons());
    observer.observe(document.body, { childList: true, subtree: true });
  }

  injectButtons() {
    document.querySelectorAll('.product-card:not([data-wishlist-injected])').forEach((card) => {
      card.setAttribute('data-wishlist-injected', '');
      const productId = card.dataset.productId;
      if (!productId) return;

      const btn = document.createElement('button');
      btn.className = 'wishlist-btn';
      btn.setAttribute('data-product-id', productId);
      btn.setAttribute('aria-label', 'Ajouter aux favoris');
      btn.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" xmlns="http://www.w3.org/2000/svg">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
      `;

      if (this.wishlist.has(productId)) {
        btn.classList.add('is-wishlisted');
      }

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggle(productId, btn);
      });

      const mediaWrapper = card.querySelector('.product-card__media, .card-media, .media');
      if (mediaWrapper) {
        mediaWrapper.style.position = 'relative';
        mediaWrapper.appendChild(btn);
      }
    });
  }

  toggle(productId, btn) {
    btn.classList.add('pop');
    btn.addEventListener('animationend', () => btn.classList.remove('pop'), { once: true });

    if (this.wishlist.has(productId)) {
      this.wishlist.delete(productId);
      btn.classList.remove('is-wishlisted');
      btn.setAttribute('aria-label', 'Ajouter aux favoris');
      GlowUpToast.show('💔 Retiré des favoris');
    } else {
      this.wishlist.add(productId);
      btn.classList.add('is-wishlisted');
      btn.setAttribute('aria-label', 'Retirer des favoris');
      GlowUpToast.show('❤️ Ajouté aux favoris !');
    }

    localStorage.setItem(this.storageKey, JSON.stringify([...this.wishlist]));
    this.updateCounter();
  }

  updateCounter() {
    const count = this.wishlist.size;
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  getAll() {
    return [...this.wishlist];
  }
}

// ── 5. TOAST NOTIFICATIONS ────────────────────────────────────
class GlowUpToast {
  static show(message, duration = 3000) {
    let toast = document.querySelector('.glowup-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'glowup-toast';
      toast.innerHTML = '<span class="glowup-toast__icon"></span><span class="glowup-toast__message"></span>';
      document.body.appendChild(toast);
    }

    const [icon, msg] = [toast.querySelector('.glowup-toast__icon'), toast.querySelector('.glowup-toast__message')];
    const parts = message.split(' ');
    icon.textContent = parts[0];
    msg.textContent = parts.slice(1).join(' ');

    toast.classList.add('is-visible');
    clearTimeout(toast._timeout);
    toast._timeout = setTimeout(() => toast.classList.remove('is-visible'), duration);
  }
}

// ── 6. POP-UP PROMOTIONNEL ────────────────────────────────────
class GlowUpPopup {
  constructor(options = {}) {
    this.options = {
      delay: options.delay || 5000,           // 5s avant affichage
      cookieDays: options.cookieDays || 3,    // Ne plus montrer pendant 3 jours
      exitIntent: options.exitIntent !== false, // Afficher à l'intention de quitter
      scrollPercent: options.scrollPercent || 40, // Afficher après 40% scroll
      cookieKey: options.cookieKey || 'glowup-popup-shown',
      ...options,
    };
    this.shown = false;
    this.popup = null;
    this.init();
  }

  init() {
    // Vérifier si déjà montré récemment
    if (this.getCookie(this.options.cookieKey)) return;

    this.buildPopup();

    // Déclencheurs
    if (this.options.delay) {
      setTimeout(() => this.show(), this.options.delay);
    }

    if (this.options.exitIntent) {
      document.addEventListener('mouseleave', (e) => {
        if (e.clientY <= 0) this.show();
      });
    }

    if (this.options.scrollPercent) {
      window.addEventListener('scroll', () => {
        const scrolled = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
        if (scrolled >= this.options.scrollPercent) this.show();
      }, { passive: true });
    }
  }

  buildPopup() {
    const overlay = document.createElement('div');
    overlay.className = 'glowup-popup-overlay';
    overlay.style.cssText = `
      position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 99998;
      display: flex; align-items: center; justify-content: center;
      opacity: 0; transition: opacity 0.4s ease; pointer-events: none;
      backdrop-filter: blur(4px);
    `;

    overlay.innerHTML = `
      <div class="glowup-popup" style="
        background: var(--color-background, #fff);
        color: var(--color-foreground, #111);
        border-radius: 16px;
        padding: 40px 32px;
        max-width: 420px;
        width: 90%;
        position: relative;
        transform: translateY(20px) scale(0.95);
        transition: transform 0.4s cubic-bezier(0.34,1.56,0.64,1);
        text-align: center;
        box-shadow: 0 24px 60px rgba(0,0,0,0.3);
      ">
        <button class="glowup-popup__close" style="
          position: absolute; top: 12px; right: 12px;
          background: none; border: none; font-size: 24px;
          cursor: pointer; color: currentColor; opacity: 0.5;
          padding: 4px; line-height: 1; border-radius: 50%;
          transition: opacity 0.2s, background 0.2s;
        " aria-label="Fermer">×</button>

        <div class="glowup-popup__badge" style="
          background: linear-gradient(135deg, #ff6b6b, #ffa500);
          color: white; border-radius: 20px; padding: 4px 14px;
          font-size: 12px; font-weight: 700; letter-spacing: 1px;
          display: inline-block; margin-bottom: 16px; text-transform: uppercase;
        ">Ouverture prochaine</div>

        <h2 style="font-size: 28px; font-weight: 800; margin: 0 0 8px; line-height: 1.2;">
          Sois prévenue de l'ouverture ✨
        </h2>

        <p style="font-size: 15px; opacity: 0.7; margin: 0 0 24px; line-height: 1.6;">
          Inscris-toi pour être alertée de l'ouverture officielle, en priorité.
        </p>

        <form class="glowup-popup__form" style="display: flex; flex-direction: column; gap: 10px;">
          <input
            type="email"
            placeholder="Votre email..."
            required
            style="
              padding: 14px 16px; border-radius: 8px; border: 2px solid rgba(0,0,0,0.1);
              font-size: 14px; outline: none; background: transparent; color: inherit;
              transition: border-color 0.2s;
              width: 100%; box-sizing: border-box;
            "
          >
          <button type="submit" style="
            padding: 14px; border-radius: 8px; border: none;
            background: var(--color-foreground, #111); color: var(--color-background, #fff);
            font-size: 15px; font-weight: 700; cursor: pointer;
            transition: transform 0.2s, opacity 0.2s;
          ">
            Rejoindre la liste d'attente
          </button>
        </form>

        <p style="font-size: 11px; opacity: 0.4; margin-top: 12px;">
          Pas de spam. Désabonnement en 1 clic.
        </p>
      </div>
    `;

    // Events
    overlay.querySelector('.glowup-popup__close').addEventListener('click', () => this.hide());
    overlay.addEventListener('click', (e) => { if (e.target === overlay) this.hide(); });
    overlay.querySelector('.glowup-popup__form').addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit(e.target);
    });

    document.body.appendChild(overlay);
    this.popup = overlay;
  }

  show() {
    if (this.shown || !this.popup) return;
    this.shown = true;

    this.popup.style.pointerEvents = 'all';
    requestAnimationFrame(() => {
      this.popup.style.opacity = '1';
      const inner = this.popup.querySelector('.glowup-popup');
      if (inner) inner.style.transform = 'translateY(0) scale(1)';
    });
  }

  hide() {
    if (!this.popup) return;
    this.popup.style.opacity = '0';
    this.popup.style.pointerEvents = 'none';
    this.setCookie(this.options.cookieKey, '1', this.options.cookieDays);
  }

  handleSubmit(form) {
    const email = form.querySelector('input[type="email"]').value;
    const btn = form.querySelector('button[type="submit"]');
    btn.textContent = '✓ Merci ! Code envoyé par email';
    btn.style.background = '#22c55e';

    setTimeout(() => this.hide(), 2500);

    // Ici vous pouvez ajouter l'intégration Klaviyo/Mailchimp
    console.log('Newsletter signup:', email);
  }

  setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${value};expires=${expires};path=/;SameSite=Lax`;
  }

  getCookie(name) {
    return document.cookie.split('; ').find((row) => row.startsWith(name + '='));
  }
}

// ── 7. PARALLAX ──────────────────────────────────────────────
class GlowUpParallax {
  constructor() {
    this.elements = [];
    this.ticking = false;
    this.init();
  }

  init() {
    // Sur mobile, désactiver le parallax
    if (window.matchMedia('(max-width: 749px)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.collectElements();
    window.addEventListener('scroll', () => this.requestUpdate(), { passive: true });
    this.update();
  }

  collectElements() {
    document.querySelectorAll('.hero__media, .section__media-background, .parallax').forEach((el) => {
      el.classList.add('parallax-container', 'parallax-element');
      this.elements.push({ el, speed: parseFloat(el.dataset.parallaxSpeed || 0.4) });
    });
  }

  requestUpdate() {
    if (!this.ticking) {
      requestAnimationFrame(() => { this.update(); this.ticking = false; });
      this.ticking = true;
    }
  }

  update() {
    const scrollY = window.scrollY;
    this.elements.forEach(({ el, speed }) => {
      const rect = el.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2 - window.innerHeight / 2;
      el.style.transform = `translateY(${centerY * speed * -0.3}px)`;
    });
  }
}

// ── 8. RIPPLE EFFECT SUR BOUTONS ─────────────────────────────
function initRipple() {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.button, .btn, [type="submit"]');
    if (!btn) return;

    const ripple = document.createElement('span');
    ripple.className = 'ripple';
    const size = Math.max(btn.offsetWidth, btn.offsetHeight);
    const rect = btn.getBoundingClientRect();

    Object.assign(ripple.style, {
      width: `${size}px`,
      height: `${size}px`,
      left: `${e.clientX - rect.left - size / 2}px`,
      top: `${e.clientY - rect.top - size / 2}px`,
    });

    btn.appendChild(ripple);
    ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
  });
}

// ── 9. PERFORMANCE MONITORING ─────────────────────────────────
function monitorPerformance() {
  if (!window.PerformanceObserver) return;

  try {
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.entryType === 'largest-contentful-paint') {
          console.log(`[GlowUp] LCP: ${Math.round(entry.startTime)}ms`);
        }
        if (entry.entryType === 'layout-shift' && !entry.hadRecentInput) {
          console.log(`[GlowUp] CLS contribution: ${entry.value.toFixed(4)}`);
        }
      });
    });
    observer.observe({ type: 'largest-contentful-paint', buffered: true });
    observer.observe({ type: 'layout-shift', buffered: true });
  } catch (e) {
    // Pas supporté dans tous les navigateurs
  }
}

// ── 10. INITIALISATION ────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  // Performance
  document.documentElement.classList.remove('preload');
  monitorPerformance();

  // Fonctionnalités
  new GlowUpLazyLoader();
  new GlowUpScrollAnimations();
  new GlowUpDarkMode();
  new GlowUpWishlist();
  initRipple();

  // Parallax (desktop uniquement)
  new GlowUpParallax();

  // Pop-up (configurable)
  const popupConfig = window.GlowUpPopupConfig || {};
  if (popupConfig.enabled !== false) {
    setTimeout(() => new GlowUpPopup(popupConfig), 1000);
  }

  console.log('[GlowUp] ✅ Toutes les améliorations chargées !');
});

// Exposer les classes globalement
window.GlowUp = {
  Toast: GlowUpToast,
  Wishlist: null, // Sera peuplé après init
  DarkMode: null,
};





/* GLOW UP PRELAUNCH JS START */
(function () {
  function injectProductNotice() {
    if (!location.pathname.includes("/products/")) return;
    if (document.querySelector(".glow-prelaunch-card")) return;
    var anchor =
      document.querySelector(".buy-buttons-block") ||
      document.querySelector("product-form-component") ||
      document.querySelector(".product-details");
    if (!anchor) return;
    var card = document.createElement("div");
    card.className = "glow-prelaunch-card";
    card.innerHTML =
      '<p class="glow-prelaunch-card__title">\uD83D\uDDD3 Ouverture officielle bient\u00f4t</p>' +
      '<p class="glow-prelaunch-card__text">Cr\u00e9e ton compte pour \u00eatre pr\u00e9venue en priorit\u00e9.</p>' +
      '<a class="glow-prelaunch-btn" href="/account/register">\uD83D\uDD14 Pr\u00e9viens-moi \u00e0 l\u2019ouverture</a>';
    anchor.parentNode.insertBefore(card, anchor);
  }
  function run() { injectProductNotice(); }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", run);
  else run();
  document.addEventListener("shopify:section:load", run);
  setInterval(run, 1500);
})();
/* GLOW UP PRELAUNCH JS END */
